import { Link } from 'react-router-dom';
import { useListFrom } from '../../hooks/useUrlParam';
import { useRef, useEffect, useState, useCallback } from 'react';
import { formatDay } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { MedalIcon } from '../shared/Icons';
import { cornerName, personName } from '../../utils/personName';
import type { DrawStep } from '../../utils/bracketDraw';

// ── Visual bracket tree with SVG connector lines ────────────────────────────

export interface BracketBout {
  id: string; bout_number: number; stage: string; status: string; bout_date?: string;
  red_first_name?: string; red_last_name?: string;
  blue_first_name?: string; blue_last_name?: string;
  red_team_name?: string; blue_team_name?: string;
  red_source_bout_id?: string | null; blue_source_bout_id?: string | null;
  verdict_winner?: string; verdict_outcome?: string;
  /** #541 — the medal of the corner, from the server (lib/medals.ts): 1, 2, 3 or null. */
  red_medal?: 1 | 2 | 3 | null; blue_medal?: 1 | 2 | 3 | null;
}
export interface BracketSeedEntry { seed_number: number; first_name: string; last_name: string; team_name?: string; }
interface BracketTreeProps {
  /** The bouts of a bracket. Their links (`red_source_bout_id`,
   *  `blue_source_bout_id`) give the tree, so a bracket keeps the pairings
   *  it was played with, whatever the shape of today (#465). */
  bouts?: BracketBout[];
  /** A bracket with no bouts: the shape that the server sends, and the
   *  athletes in draw-position order (index 0 is position 1). */
  plan?: DrawStep[] | null;
  seats?: BracketSeedEntry[];
  fighterCount: number;
}

/** One box of the tree: a bout, or a step of the plan. `red` and `blue`
 *  are the keys of the boxes whose winners come in. */
type TreeNode = { key: string; stage: string; red: string | null; blue: string | null }
  & ({ bout: BracketBout } | { step: DrawStep; idx: number });

const STAGE_ORDER = ['R32', 'R16', 'QF', 'SF', 'Final'];

/** The columns of the tree, one for each stage, each top to bottom. A box
 *  follows the box it feeds: the red side above the blue side. */
function layout(nodes: TreeNode[]): { stage: string; items: TreeNode[] }[] {
  const byKey = new Map(nodes.map(n => [n.key, n]));
  const fed = new Set(nodes.flatMap(n => [n.red, n.blue]).filter((k): k is string => !!k));
  const columns = new Map<string, TreeNode[]>();
  const seen = new Set<string>();
  const visit = (n: TreeNode) => {
    if (seen.has(n.key)) return;
    seen.add(n.key);
    for (const k of [n.red, n.blue]) {
      const child = k ? byKey.get(k) : undefined;
      if (child) visit(child);
    }
    if (!columns.has(n.stage)) columns.set(n.stage, []);
    columns.get(n.stage)!.push(n);
  };
  nodes.filter(n => !fed.has(n.key)).forEach(visit);
  nodes.forEach(visit); // a box outside every tree still shows
  const rank = (s: string) => { const i = STAGE_ORDER.indexOf(s); return i === -1 ? STAGE_ORDER.length : i; };
  return [...columns.keys()].sort((a, b) => rank(a) - rank(b)).map(stage => ({ stage, items: columns.get(stage)! }));
}

const SC: Record<string, { hdr: string; label: string; border: string }> = {
  'Final': { hdr: 'bg-amber-50 border-amber-200', label: 'text-amber-700', border: 'border-amber-200' },
  'SF':    { hdr: 'bg-purple-50 border-purple-200', label: 'text-purple-700', border: 'border-purple-200' },
  'QF':    { hdr: 'bg-blue-50 border-blue-200', label: 'text-blue-700', border: 'border-blue-200' },
  'R16':   { hdr: 'bg-teal-50 border-teal-200', label: 'text-teal-700', border: 'border-teal-200' },
  'R32':   { hdr: 'bg-gray-50 border-gray-200', label: 'text-gray-600', border: 'border-gray-200' },
};

function Corner({ name, team, won, lost, color, medal }: { name: string; team?: string; won: boolean; lost?: boolean; color: 'red'|'blue'; medal?: 1 | 2 | 3 }) {
  const dot = color === 'red' ? 'bg-red-500' : 'bg-blue-500';
  const winCls = color === 'red' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700';
  return (
    <div data-testid="bracket-corner" data-medal={medal ?? ''} className={`px-2 py-1 flex items-center gap-1 ${won ? winCls : ''}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot} shrink-0`} />
      <div className="truncate flex-1 min-w-0">
        <span className={`truncate text-xs font-medium ${won ? '' : lost ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{name || '—'}</span>
        {team && <div className="text-[9px] text-gray-400 truncate leading-tight">{team}</div>}
      </div>
      {medal && <MedalIcon className="ml-auto w-5 h-5 shrink-0" place={medal} />}
      {won && !medal && <span data-testid="bracket-win-mark" className={`ml-auto text-[10px] font-bold ${color === 'red' ? 'text-red-500' : 'text-blue-500'}`}>W</span>}
    </div>
  );
}

function BoutNode({ bout, nodeRef, stageLabel, liveLabel }: { bout: BracketBout; nodeRef?: (el: HTMLElement | null) => void; stageLabel: (s: string) => string; liveLabel: string }) {
  const isCurrent = bout.status === 'current';
  const isScored = bout.status === 'scored';
  const listFrom = useListFrom();
  const redWon = bout.verdict_winner === 'red';
  const blueWon = bout.verdict_winner === 'blue';
  // #541 — the medal comes from the server (lib/medals.ts): the place in the
  // bracket and the IFMA eligibility (§52.1, §30.5, §11.3.1). A Walk Over
  // champion who never fought wins the bout ("W") and gets no medal. The page
  // derives no medal of its own. The Ratings tab reads the same rule, and it
  // also leaves U8 and U10 out of the team points.
  const redMedal = isScored ? (bout.red_medal ?? undefined) : undefined;
  const blueMedal = isScored ? (bout.blue_medal ?? undefined) : undefined;
  const s = SC[bout.stage] || { hdr: 'bg-gray-50 border-gray-200', label: 'text-gray-500', border: 'border-gray-200' };
  return (
    <Link ref={nodeRef} to={`/bouts/${bout.id}`} state={listFrom} data-bout-idx={bout.bout_number}
      className={`block rounded-lg border overflow-hidden hover:shadow-md transition-shadow ${
        isCurrent ? 'ring-2 ring-green-400 border-green-300' : s.border}`}>
      <div className={`px-2 py-0.5 ${s.hdr} border-b flex items-center justify-between`}>
        <span className={`font-semibold text-[10px] ${s.label}`}>{stageLabel(bout.stage)}</span>
        <span className="text-[10px] text-gray-400">
          #{bout.bout_number}
          {bout.bout_date && ` · ${formatDay(bout.bout_date, 'dd MMM')}`}
        </span>
      </div>
      <Corner name={cornerName(bout, 'red')} team={bout.red_team_name} won={redWon} lost={blueWon} color="red" medal={redMedal} />
      <div className="border-t border-gray-100" />
      <Corner name={cornerName(bout, 'blue')} team={bout.blue_team_name} won={blueWon} lost={redWon} color="blue" medal={blueMedal} />
      {bout.status === 'scored' && bout.verdict_outcome && (
        <div className="px-2 py-0.5 bg-gray-50 border-t border-gray-100 text-center">
          <span className="text-[10px] font-semibold text-gray-500">{bout.verdict_outcome}</span>
        </div>
      )}
      {isCurrent && (
        <div className="px-2 py-0.5 bg-green-50 border-t border-green-200 text-center">
          <span className="text-[10px] font-bold text-green-700">{liveLabel}</span>
        </div>
      )}
    </Link>
  );
}

function DraftNode({ step, idx, seats, stageLabel }: { step: DrawStep; idx: number; seats: BracketSeedEntry[]; stageLabel: (s: string) => string }) {
  const s = SC[step.stage] || { hdr: 'bg-gray-50 border-gray-200', label: 'text-gray-500', border: 'border-gray-200' };
  // A corner holds the athlete on a draw position, or the winner of an
  // earlier box of this plan (`#n` in the box header).
  const corner = (pos: number | null, from: number | null) => {
    const a = pos ? seats[pos - 1] : null;
    return a
      ? { name: `${pos}. ${personName(a.first_name, a.last_name)}`, team: a.team_name }
      : { name: `Winner #${(from ?? 0) + 1}`, team: undefined };
  };
  const red = corner(step.redSeed, step.redSourceIdx);
  const blue = corner(step.blueSeed, step.blueSourceIdx);
  return (
    <div className={`rounded-lg border overflow-hidden ${s.border}`}>
      <div className={`px-2 py-0.5 ${s.hdr} border-b flex items-center justify-between`}>
        <span className={`font-semibold text-[10px] ${s.label}`}>{stageLabel(step.stage)}</span>
        <span className="text-[10px] text-gray-400">#{idx + 1}</span>
      </div>
      <Corner name={red.name} team={red.team} won={false} color="red" />
      <div className="border-t border-gray-100" />
      <Corner name={blue.name} team={blue.team} won={false} color="blue" />
    </div>
  );
}

// ── SVG connector lines ─────────────────────────────────────────────────────
function ConnectorLines({ containerRef, connections }: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  connections: { from: string; to: string }[];
}) {
  const [lines, setLines] = useState<{ x1: number; y1: number; x2: number; y2: number; link: string }[]>([]);
  const [vb, setVb] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  const calc = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const nodes = container.querySelectorAll('[data-node-key]');
    const nodeMap = new Map<string, DOMRect>();
    nodes.forEach(n => {
      const key = n.getAttribute('data-node-key');
      if (key) nodeMap.set(key, n.getBoundingClientRect());
    });

    const newLines: typeof lines = [];
    for (const c of connections) {
      const from = nodeMap.get(c.from);
      const to = nodeMap.get(c.to);
      if (!from || !to) continue;
      const x1 = from.right - rect.left;
      const y1 = from.top + from.height / 2 - rect.top;
      const x2 = to.left - rect.left;
      const y2 = to.top + to.height / 2 - rect.top;
      newLines.push({ x1, y1, x2, y2, link: `${c.from}>${c.to}` });
    }
    setLines(newLines);
    setVb({ w: rect.width, h: rect.height });
  }, [containerRef, connections]);

  useEffect(() => {
    calc();
    const timer = setTimeout(calc, 100); // recalc after layout settles
    const container = containerRef.current;
    const ro = container ? new ResizeObserver(() => calc()) : null;
    if (container && ro) ro.observe(container);
    return () => {
      clearTimeout(timer);
      ro?.disconnect();
    };
  }, [calc, containerRef]);

  if (!lines.length || !vb.w || !vb.h) return null;
  // viewBox + preserveAspectRatio="none": SVG content scales with container.
  // Print reflows the container to a different width but the bracket's flex
  // columns scale proportionally, so line endpoints land at the correct
  // relative positions regardless of screen-vs-print viewport.
  return (
    <svg className="absolute inset-0 pointer-events-none"
         viewBox={`0 0 ${vb.w} ${vb.h}`}
         preserveAspectRatio="none"
         style={{ width: '100%', height: '100%' }}>
      {lines.map((l, i) => {
        const midX = (l.x1 + l.x2) / 2;
        return (
          <path key={i} data-link={l.link}
            d={`M${l.x1},${l.y1} C${midX},${l.y1} ${midX},${l.y2} ${l.x2},${l.y2}`}
            fill="none" stroke="#d1d5db" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        );
      })}
    </svg>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function BracketTree({ bouts = [], plan, seats = [], fighterCount }: BracketTreeProps) {
  const { stageLabel, t } = useLabels();
  const containerRef = useRef<HTMLDivElement>(null);

  // #465 — the bouts, when the bracket has them; else the plan. The browser
  // builds no shape of its own: the old copy of the server's shape redrew
  // every played bracket in the shape of the day.
  const nodes: TreeNode[] = bouts.length
    ? bouts.map(b => ({ key: b.id, stage: b.stage, red: b.red_source_bout_id ?? null, blue: b.blue_source_bout_id ?? null, bout: b }))
    : (plan ?? []).map((step, idx) => ({
        key: `p${idx}`, stage: step.stage, step, idx,
        red: step.redSourceIdx !== null ? `p${step.redSourceIdx}` : null,
        blue: step.blueSourceIdx !== null ? `p${step.blueSourceIdx}` : null,
      }));
  if (!nodes.length) return <p className="text-sm text-gray-400 text-center py-4">{t.tournament.noBracketData}</p>;

  const keys = new Set(nodes.map(n => n.key));
  const connections = nodes.flatMap(n => [n.red, n.blue]
    .filter((k): k is string => !!k && keys.has(k))
    .map(from => ({ from, to: n.key })));
  const rounds = layout(nodes);

  return (
    <div className="overflow-x-auto">
      <div ref={containerRef} className="relative flex items-stretch w-full min-w-fit" style={{ minHeight: Math.max(fighterCount * 50, 120) }}>
        <ConnectorLines containerRef={containerRef} connections={connections} />
        {rounds.map((round, ri) => {
          const count = round.items.length;
          const gap = count >= 4 ? 'gap-2' : count >= 2 ? 'gap-8' : 'gap-0';
          return (
            <div key={round.stage} className="flex-1 flex flex-col min-w-[150px] relative z-10" style={{ marginLeft: ri > 0 ? 32 : 0 }}>
              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2 text-center">{stageLabel(round.stage)}</div>
              <div className={`flex flex-col justify-around flex-1 ${gap}`}>
                {round.items.map(n => (
                  <div key={n.key} data-node-key={n.key}>
                    {'bout' in n
                      ? <BoutNode bout={n.bout} stageLabel={stageLabel} liveLabel={t.common.live} />
                      : <DraftNode step={n.step} idx={n.idx} seats={seats} stageLabel={stageLabel} />}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
